import cn from "classnames"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import type Sign from "../app/sign"
import ClientData from "@/common/client_data"
import { actions } from "@/signomatic/store/actions"
import { CloseLarge, InfoFill } from "@/skylticons"

interface RestoreBannerProps {
  sign: Sign
}

const RestoreBanner: React.FC<RestoreBannerProps> = ({ sign }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [showBanner, setShowBanner] = useState(false)

  const handleRestoreSign = () => {
    setShowBanner(false)
    const restored = sign.restorePreviousSign()

    if (!restored) {
      return
    }

    const { metaProduct, signType } = restored

    if (ClientData.editing) {
      dispatch(actions.removeEditing())
      ClientData.update({ editing: undefined })
    }

    if (metaProduct) {
      navigate(t("route.signomatic") + t(`route.${metaProduct}`))
    } else {
      navigate(t("route.signomatic") + t(`route.${signType}`))
    }
  }

  const handleCloseBanner = () => {
    setShowBanner(false)
    sign.removePreviousSignData()
  }

  useEffect(() => {
    if (sign.canRestorePreviousSign) {
      setShowBanner(true)
    } else {
      setShowBanner(false)
    }
  }, [sign.canRestorePreviousSign])

  return (
    <div
      id="restore-banner"
      className={cn("absolute right-0 top-[67px] z-20 lg:right-3 lg:top-[60px] lg:w-[375px]", {
        hidden: !showBanner,
      })}
    >
      <div className="mx-2 flex gap-3 rounded-lg border bg-info-bg p-3 lg:mx-0">
        <InfoFill className="text-info" />
        <div>
          <div className="flex flex-col text-base">
            <div className="font-semibold">
              {ClientData.editing ? t("signomatic.cart_sign_edit") : t("signomatic.restore_banner_title")}
            </div>
            <div>{ClientData.editing ? t("signomatic.cart_sign_update") : t("signomatic.restore_banner_text")}</div>
          </div>
          {!ClientData.editing && (
            <button
              id="restore-sign"
              data-gtm="restore"
              className="button button-primary mb-0 mt-2 h-9 px-6"
              type="button"
              onClick={() => handleRestoreSign()}
            >
              {t("signomatic.restore_banner_button")}
            </button>
          )}
        </div>
        <button
          className="button button-subtle icon-only -mr-3 -mt-3"
          type="button"
          onClick={() => handleCloseBanner()}
        >
          <CloseLarge />
        </button>
      </div>
    </div>
  )
}

export default RestoreBanner
